import React, { useCallback, useEffect, useRef, useState } from "react";

import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/legacy/build/pdf.mjs";
import { EventBus, PDFFindController, PDFLinkService, PDFViewer } from "pdfjs-dist/web/pdf_viewer.mjs";
import "pdfjs-dist/web/pdf_viewer.css";
import { saveAs } from "file-saver";
import styled, { keyframes } from "styled-components";

import Icon from "components/atoms/Icon";
import Checkbox from "components/atoms/input/Checkbox";
import Text from "components/atoms/text/Text";

import Constants from "utils/constants";
import { getReviewPageLayout, getReviewPdfFile } from "utils/request/review";

GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const ViewerRoot = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    background: #f7f7f7;
`;

const ViewerBody = styled.div`
    position: relative;
    flex: 1;
    min-height: 0;
`;

const TopToolbar = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 40px;
    padding: 6px 12px;
    border-bottom: 1px solid #d9dde2;
    background: #ffffff;
    flex-shrink: 0;
    z-index: 20;
    overflow: visible;
`;

const ToolbarCenter = styled.div`
    position: absolute;
    left: 50%;
    top: 50%;
    display: flex;
    align-items: center;
    min-width: 0;
    gap: 6px;
    transform: translate(-50%, -50%);
`;

const ToolbarSide = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
    gap: 6px;
    margin-left: auto;
`;

const ToolbarGroup = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const ToolbarDivider = styled.div`
    width: 1px;
    height: 22px;
    background: #d9dde2;
`;

const ToolbarButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 26px;
    height: 24px;
    padding: 0 6px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: var(--color-SubBlack);
    font-size: 1.2rem;
    cursor: pointer;

    &:hover:not(:disabled) {
        background: #eef2f5;
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const ToolbarInput = styled.input`
    width: 34px;
    height: 24px;
    border: 1px solid #d9dde2;
    border-radius: 4px;
    background: #ffffff;
    color: var(--color-SubBlack);
    font-size: 1.2rem;
    text-align: center;

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    &[type="number"] {
        -moz-appearance: textfield;
    }
`;

const ToolbarSelect = styled.select`
    width: 74px;
    height: 24px;
    padding: 0 6px;
    border: 1px solid #d9dde2;
    border-radius: 4px;
    background: #ffffff;
    color: var(--color-SubBlack);
    font-size: 1.2rem;
    text-align: center;
    text-align-last: center;
    cursor: pointer;

    &:hover {
        background: #eef2f5;
    }
`;

const ToolbarText = styled(Text)`
    font-size: 1.2rem;
    color: var(--color-SubBlack);
`;

const pulseStatus = keyframes`
    0% { opacity: 0.45; }
    50% { opacity: 1; }
    100% { opacity: 0.45; }
`;

const ToolbarStatus = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    animation: ${pulseStatus} 1.2s ease-in-out infinite;
    cursor: wait;
`;

const ToolbarStatusDot = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--color-Key);
`;

const ToolbarStatusText = styled(Text)`
    font-size: 1.2rem;
    color: var(--color-Key);
    white-space: nowrap;
`;

const SearchWrap = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
`;

const SearchPopover = styled.div`
    position: absolute;
    left: 50%;
    top: calc(100% + 8px);
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 6px;
    border: 1px solid #d9dde2;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.16);
    transform: translateX(-50%);
    z-index: 30;
`;

const SearchInput = styled.input`
    width: 180px;
    height: 28px;
    padding: 0 8px;
    border: 1px solid #d9dde2;
    border-radius: 4px;
    color: var(--color-SubBlack);
    font-size: 1.2rem;
`;

const ViewerContainer = styled.div`
    position: absolute;
    inset: 0;
    overflow: auto;
    background: #f7f7f7;
    cursor: ${({ $isLayoutPending }) => ($isLayoutPending ? "wait" : "default")};

    .pdfViewer {
        --scale-factor: 1;
        position: relative;
        padding: 16px 0;
    }

    .pdfViewer .page {
        position: relative;
        margin: 0 auto 16px auto;
        border: 0;
        outline: none;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
    }

    .pdfViewer .page:focus {
        outline: none;
    }

    .pdfViewer .textLayer span {
        cursor: ${({ $isLayoutPending }) => ($isLayoutPending ? "wait" : "text")};
    }

    .pdfViewer .textLayer ::selection {
        background: ${({ $selectionColor }) =>
            `color-mix(in srgb, ${$selectionColor || "var(--color-HighlightRed)"} 42%, transparent)`};
    }
`;

const ViewerElement = styled.div``;

const OverlayLayer = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    z-index: 20;
    width: ${({ $width }) => $width}px;
    height: ${({ $height }) => $height}px;
    pointer-events: none;
`;

const LayoutHoverBox = styled.div`
    position: absolute;
    z-index: 20;
    border-radius: 2px;
    background: rgba(0, 154, 126, 0.08);
    pointer-events: none;
`;

const ReviewBox = styled.div`
    position: absolute;
    border: ${({ $active, $color }) => ($active ? `1px solid ${$color || "var(--color-Key)"}` : "none")};
    background-color: ${({ $hovered, $filled, $color }) =>
        $hovered || $filled ? `color-mix(in srgb, ${$color || "var(--color-Key)"} 24%, transparent)` : "transparent"};
    cursor: ${({ $hovered }) => ($hovered ? "pointer" : "default")};
    pointer-events: auto;
`;

const DragBox = styled.div`
    position: absolute;
    border: 1px dashed var(--color-Key);
    background: rgba(0, 154, 126, 0.16);
    pointer-events: none;
`;

const PendingTextSelectionBox = styled.div`
    position: absolute;
    z-index: 21;
    border-radius: 1px;
    background-color: ${({ $color }) =>
        `color-mix(in srgb, ${$color || "var(--color-HighlightRed)"} 38%, transparent)`};
    pointer-events: none;
`;

const ReviewTextSelectionBox = styled.div`
    position: absolute;
    z-index: 21;
    border-radius: 1px;
    background-color: ${({ $hovered, $color }) =>
        `color-mix(in srgb, ${$color || "var(--color-Key)"} ${$hovered ? 34 : 24}%, transparent)`};
    outline: ${({ $hovered, $color }) => ($hovered ? `1px solid ${$color || "var(--color-Key)"}` : "none")};
    cursor: pointer;
    pointer-events: auto;
`;

const DimOverlay = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    z-index: 22;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    pointer-events: auto;
`;

const HighlightBox = styled.div`
    position: absolute;
    z-index: 23;
    background: rgba(255, 255, 0, 0.3);
    border: 2px solid #ffd600;
    pointer-events: none;
`;

const BoxButtonGroup = styled.div`
    position: absolute;
    z-index: 24;
    display: flex;
    gap: 2px;
    pointer-events: auto;
`;

const BoxButtonText = styled(Text)`
    padding: 2px 4px;
    cursor: pointer;
    color: var(--color-White);
    background-color: var(--color-Warn);
    border-radius: 2px;

    &:hover {
        background-color: var(--color-WarnHover);
    }
`;

const SelectionMessagePanel = styled.div`
    position: absolute;
    z-index: 25;
    width: 300px;
    max-height: 240px;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--color-Line);
    border-radius: 4px;
    background-color: var(--color-White);
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.16);
    pointer-events: auto;
`;

const SelectionMessagePanelHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    border-bottom: 1px solid var(--color-Line);
`;

const SelectionMessagePanelTitle = styled(Text).attrs({
    variant: "body-sm-500",
    color: "var(--color-SubBlack)",
})``;

const SelectionMessagePanelMeta = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})`
    margin-left: auto;
`;

const SelectionMessageList = styled.div`
    display: flex;
    flex-direction: column;
    max-height: 192px;
    overflow-y: auto;
`;

const SelectionMessageItem = styled.button`
    width: 100%;
    padding: 8px 10px;
    border: 0;
    border-bottom: 1px solid var(--color-Line);
    background-color: transparent;
    text-align: left;
    cursor: pointer;

    &:hover {
        background-color: var(--color-ListHover);
    }

    &:last-child {
        border-bottom: 0;
    }
`;

const SelectionMessageAuthor = styled(Text).attrs({
    variant: "body-sm-500",
    color: "var(--color-SubBlack)",
})``;

const SelectionMessagePreview = styled(Text).attrs({
    variant: "body-sm-400",
    color: "var(--color-Grey1)",
})`
    display: -webkit-box;
    margin-top: 4px;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const ViewerStatus = styled.div`
    position: absolute;
    inset: 0;
    z-index: 30;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 240px;
    color: var(--color-Grey1);
    font-size: 1.4rem;
`;

const SelectionComposer = styled.div`
    position: absolute;
    z-index: 35;
    width: 280px;
    padding: 10px;
    border: 1px solid #d9dde2;
    border-radius: 6px;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
    pointer-events: auto;
`;

const ComposerHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
`;

const ComposerHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    min-width: 0;
    gap: 8px;
`;

const ComposerTitle = styled(Text)`
    color: var(--color-SubBlack);
    font-size: 1.3rem;
    font-weight: 600;
`;

const ComposerCloseButton = styled.button`
    border: 0;
    background: transparent;
    color: var(--color-Grey1);
    cursor: pointer;
    font-size: 1.4rem;
`;

const ComposerColorPalette = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const ComposerColorButton = styled.button`
    width: 16px;
    height: 16px;
    padding: 0;
    border: ${({ $active }) => ($active ? "2px solid var(--color-Black)" : "2px solid #ffffff")};
    border-radius: 50%;
    background: ${({ $color }) => $color};
    box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.12);
    cursor: pointer;
`;

const ComposerTextarea = styled.textarea`
    width: 100%;
    min-height: 72px;
    padding: 8px;
    resize: vertical;
    border: 1px solid #d9dde2;
    border-radius: 4px;
    color: var(--color-SubBlack);
    font-size: 1.3rem;
    line-height: 1.45;
    outline: none;

    &:focus {
        border-color: var(--color-Key);
    }
`;

const ComposerFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 6px;
    margin-top: 8px;
`;

const ComposerOptionRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 8px;
`;

const ComposerButton = styled.button`
    height: 28px;
    padding: 0 12px;
    border: 1px solid ${({ $primary }) => ($primary ? "var(--color-Key)" : "#d9dde2")};
    border-radius: 4px;
    background: ${({ $primary }) => ($primary ? "var(--color-Key)" : "#ffffff")};
    color: ${({ $primary }) => ($primary ? "#ffffff" : "var(--color-SubBlack)")};
    cursor: pointer;
    font-size: 1.2rem;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.45;
    }
`;

const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const LAYOUT_HOVER_EXCLUDED_LABELS = new Set(["Page-header", "Page-footer"]);
const MIN_DRAG_DISTANCE = 0.01;
const COMPOSER_WIDTH = 280;
const COMPOSER_HEIGHT = 190;
const HIGHLIGHT_COLORS = [
    "var(--color-HighlightRed)",
    "var(--color-HighlightBlue)",
    "var(--color-HighlightYellow)",
    "var(--color-HighlightGreen)",
    "var(--color-HighlightPurple)",
];

function PDFJsReviewViewer(props) {
    const {
        fileUrl,
        referenceFileUuid,
        page,
        maxPage,
        boxes,
        setBoxes,
        threads,
        selectedBox,
        setSelectedBox,
        searchBox,
        setSearchBox,
        setMaxPage,
        onPageChange,
        chatStart,
        parentMessageId,
        profiles,
        onSelectReviewMessage,
    } = props;

    const containerRef = useRef(null);
    const viewerRef = useRef(null);
    const pdfViewerRef = useRef(null);
    const loadingTaskRef = useRef(null);
    const eventBusRef = useRef(null);
    const pdfBufferRef = useRef(null);
    const searchWrapRef = useRef(null);
    const layoutCacheRef = useRef(new Map());
    const layoutPendingRef = useRef(new Set());
    const dragStartRef = useRef(null);
    const selectionInProgressRef = useRef(false);
    const pageRef = useRef(page);
    const onPageChangeRef = useRef(onPageChange);
    const scaleValueRef = useRef("page-width");

    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [pageInput, setPageInput] = useState(String(page));
    const [scaleValue, setScaleValue] = useState("page-width");
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState("");
    const [overlaySize, setOverlaySize] = useState({ width: 1, height: 1 });
    const [pageRects, setPageRects] = useState({});
    const [layoutBoxesByPage, setLayoutBoxesByPage] = useState({});
    const [hoveredLayoutBox, setHoveredLayoutBox] = useState(null);
    const [hoveredBoxIdx, setHoveredBoxIdx] = useState(null);
    const [dragBox, setDragBox] = useState(null);
    const [dragPage, setDragPage] = useState(null);
    const [pendingSelection, setPendingSelection] = useState(null);
    const [composerMessage, setComposerMessage] = useState("");
    const [selectionColor, setSelectionColor] = useState(HIGHLIGHT_COLORS[0]);
    const [composerAutoResponse, setComposerAutoResponse] = useState(true);
    const isLayoutPending = Boolean(referenceFileUuid && page && !layoutCacheRef.current.has(page));

    const requestLayoutPage = useCallback(
        (pageNumber) => {
            if (!referenceFileUuid || !pageNumber || pageNumber < 1) return;
            if (maxPage > 1 && pageNumber > maxPage) return;
            if (layoutCacheRef.current.has(pageNumber) || layoutPendingRef.current.has(pageNumber)) return;

            layoutPendingRef.current.add(pageNumber);
            getReviewPageLayout({ reference_file_uuid: referenceFileUuid, page: pageNumber })
                .then((layout) => {
                    const layoutPage = layout.page || pageNumber;
                    const boxes = Array.isArray(layout.boxes) ? layout.boxes : [];
                    layoutCacheRef.current.set(layoutPage, boxes);
                    setLayoutBoxesByPage((prev) => ({
                        ...prev,
                        [layoutPage]: boxes,
                    }));
                })
                .catch((error) => {
                    console.error("[PDF layout boxes] failed", error);
                    layoutCacheRef.current.set(pageNumber, []);
                    setLayoutBoxesByPage((prev) => ({
                        ...prev,
                        [pageNumber]: [],
                    }));
                })
                .finally(() => {
                    layoutPendingRef.current.delete(pageNumber);
                });
        },
        [maxPage, referenceFileUuid],
    );

    useEffect(() => {
        pageRef.current = page;
        setPageInput(String(page));
    }, [page]);

    useEffect(() => {
        layoutCacheRef.current.clear();
        layoutPendingRef.current.clear();
        setLayoutBoxesByPage({});
        setHoveredLayoutBox(null);
    }, [referenceFileUuid]);

    useEffect(() => {
        onPageChangeRef.current = onPageChange;
    }, [onPageChange]);

    useEffect(() => {
        scaleValueRef.current = scaleValue;
    }, [scaleValue]);

    useEffect(() => {
        if (!isSearchOpen) return undefined;

        const handleOutsideClick = (event) => {
            if (searchWrapRef.current?.contains(event.target)) return;
            setIsSearchOpen(false);
        };

        window.addEventListener("mousedown", handleOutsideClick);
        return () => window.removeEventListener("mousedown", handleOutsideClick);
    }, [isSearchOpen]);

    const updatePageRects = useCallback(() => {
        const container = containerRef.current;
        const viewer = viewerRef.current;
        if (!container || !viewer) return;

        const viewerRect = viewer.getBoundingClientRect();
        const nextRects = {};
        let contentBottom = 0;
        let contentRight = 0;
        viewer.querySelectorAll(".page[data-page-number]").forEach((pageElement) => {
            const pageNumber = Number(pageElement.dataset.pageNumber);
            const rect = pageElement.getBoundingClientRect();
            const left = rect.left - viewerRect.left;
            const top = rect.top - viewerRect.top;
            nextRects[pageNumber] = {
                left,
                top,
                width: rect.width,
                height: rect.height,
            };
            contentRight = Math.max(contentRight, left + rect.width);
            contentBottom = Math.max(contentBottom, top + rect.height);
        });

        setOverlaySize({
            width: Math.max(contentRight, 1),
            height: Math.max(contentBottom, 1),
        });
        setPageRects(nextRects);
    }, []);

    useEffect(() => {
        if (!referenceFileUuid || !page) return undefined;

        const debounceTimer = window.setTimeout(() => {
            requestLayoutPage(page);
        }, 350);

        return () => {
            window.clearTimeout(debounceTimer);
        };
    }, [page, referenceFileUuid, requestLayoutPage]);

    const getPageElement = useCallback((pageNumber) => {
        return viewerRef.current?.querySelector(`.page[data-page-number="${pageNumber}"]`) || null;
    }, []);

    const getPageElementFromTarget = useCallback((target) => {
        return target?.closest?.(".page[data-page-number]") || null;
    }, []);

    const getPointerInfo = useCallback(
        (event) => {
            const pageElement = getPageElementFromTarget(event.target);
            if (!pageElement) return null;

            const rect = pageElement.getBoundingClientRect();
            const pageNumber = Number(pageElement.dataset.pageNumber);
            return {
                pageNumber,
                x: clamp((event.clientX - rect.left) / rect.width),
                y: clamp((event.clientY - rect.top) / rect.height),
            };
        },
        [getPageElementFromTarget],
    );

    const getLayoutBoxAtPointer = useCallback(
        (pointerInfo) => {
            if (!pointerInfo) return null;

            return (
                (layoutBoxesByPage[pointerInfo.pageNumber] || [])
                    .filter((box) => !LAYOUT_HOVER_EXCLUDED_LABELS.has(box.label))
                    .filter((box) => {
                        const [x1, y1, x2, y2] = box.bbox || [];
                        return pointerInfo.x >= x1 && pointerInfo.x <= x2 && pointerInfo.y >= y1 && pointerInfo.y <= y2;
                    })
                    .sort((a, b) => {
                        const [ax1, ay1, ax2, ay2] = a.bbox;
                        const [bx1, by1, bx2, by2] = b.bbox;
                        return (ax2 - ax1) * (ay2 - ay1) - (bx2 - bx1) * (by2 - by1);
                    })[0] || null
            );
        },
        [layoutBoxesByPage],
    );

    const getSelectedTextSpans = useCallback(() => {
        const selection = window.getSelection();
        const selectedText = selection?.toString?.().trim() || "";
        if (!selectedText || !selection.rangeCount) return { text: selectedText, spans: [] };

        const spans = [];
        for (let rangeIndex = 0; rangeIndex < selection.rangeCount; rangeIndex += 1) {
            const range = selection.getRangeAt(rangeIndex);
            const pageElements = viewerRef.current?.querySelectorAll(".page[data-page-number]") || [];

            pageElements.forEach((pageElement) => {
                const pageNumber = Number(pageElement.dataset.pageNumber);
                const pageRect = pageElement.getBoundingClientRect();
                pageElement.querySelectorAll(".textLayer span").forEach((span, spanIndex) => {
                    const text = span.textContent?.trim();
                    if (!text) return;

                    try {
                        if (!range.intersectsNode(span)) return;
                    } catch (error) {
                        return;
                    }

                    const rect = span.getBoundingClientRect();
                    if (rect.width < 1 || rect.height < 1) return;
                    spans.push({
                        page: pageNumber,
                        index: spanIndex,
                        text,
                        bbox: [
                            clamp((rect.left - pageRect.left) / pageRect.width),
                            clamp((rect.top - pageRect.top) / pageRect.height),
                            clamp((rect.right - pageRect.left) / pageRect.width),
                            clamp((rect.bottom - pageRect.top) / pageRect.height),
                        ],
                    });
                });
            });
        }

        return { text: selectedText, spans };
    }, []);

    const getTextSpansUnionBbox = useCallback((spans) => {
        if (!spans?.length) return null;

        return spans.reduce(
            (union, span) => {
                const [x1, y1, x2, y2] = span.bbox;
                return [Math.min(union[0], x1), Math.min(union[1], y1), Math.max(union[2], x2), Math.max(union[3], y2)];
            },
            [1, 1, 0, 0],
        );
    }, []);

    const showSelectionComposer = useCallback(
        (selection) => {
            setPendingSelection({
                ...selection,
                color: selectionColor,
            });
            setComposerMessage("");
        },
        [selectionColor],
    );

    const closeSelectionComposer = useCallback(() => {
        setPendingSelection(null);
        setComposerMessage("");
        setSelectedBox(null);
        window.getSelection()?.removeAllRanges?.();
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }, [setSelectedBox]);

    const getBoxImageFromCanvas = useCallback(
        (pageNumber, bbox) => {
            const canvas = getPageElement(pageNumber)?.querySelector("canvas");
            if (!canvas || !bbox) return null;

            const [x1, y1, x2, y2] = bbox;
            const sx = Math.round(clamp(x1) * canvas.width);
            const sy = Math.round(clamp(y1) * canvas.height);
            const sw = Math.max(1, Math.round((clamp(x2) - clamp(x1)) * canvas.width));
            const sh = Math.max(1, Math.round((clamp(y2) - clamp(y1)) * canvas.height));

            const cropCanvas = document.createElement("canvas");
            cropCanvas.width = sw;
            cropCanvas.height = sh;
            const cropCtx = cropCanvas.getContext("2d");
            cropCtx.drawImage(canvas, sx, sy, sw, sh, 0, 0, sw, sh);

            return {
                base64: cropCanvas.toDataURL("image/png"),
                width: sw,
                height: sh,
            };
        },
        [getPageElement],
    );

    const createTemporaryBox = useCallback(
        async (pageNumber, bbox, extraData = {}) => {
            const normalizedBox = bbox.map((value) => clamp(value));
            const newBox = {
                id: Constants.REVIEW_NEW_SELECTION_ID,
                page: pageNumber,
                selection_type: extraData.selection_type || "area",
                data: {
                    label: "Label",
                    bbox: normalizedBox,
                    ...extraData,
                },
            };
            setBoxes((prev) => [...prev.filter((box) => box.id !== Constants.REVIEW_NEW_SELECTION_ID), newBox]);
            setSelectedBox({
                ...newBox,
                box_image: getBoxImageFromCanvas(pageNumber, normalizedBox),
            });
        },
        [getBoxImageFromCanvas, setBoxes, setSelectedBox],
    );

    const updateTemporaryBoxColor = useCallback(
        (color) => {
            setBoxes((prevBoxes) =>
                prevBoxes.map((box) =>
                    box.id === Constants.REVIEW_NEW_SELECTION_ID
                        ? {
                              ...box,
                              data: {
                                  ...box.data,
                                  color,
                              },
                          }
                        : box,
                ),
            );
        },
        [setBoxes],
    );

    const handleSelectionColorChange = useCallback(
        (color) => {
            setSelectionColor(color);
            setPendingSelection((prevSelection) =>
                prevSelection
                    ? {
                          ...prevSelection,
                          color,
                      }
                    : prevSelection,
            );
            updateTemporaryBoxColor(color);
        },
        [updateTemporaryBoxColor],
    );

    useEffect(() => {
        const container = containerRef.current;
        const viewer = viewerRef.current;
        if (!container || !viewer) return undefined;

        const eventBus = new EventBus();
        const linkService = new PDFLinkService({ eventBus });
        const findController = new PDFFindController({ linkService, eventBus });
        const pdfViewer = new PDFViewer({
            container,
            viewer,
            eventBus,
            linkService,
            findController,
            removePageBorders: false,
        });

        linkService.setViewer(pdfViewer);
        eventBusRef.current = eventBus;
        pdfViewerRef.current = pdfViewer;

        const handlePagesInit = () => {
            pdfViewer.currentScaleValue = scaleValueRef.current;
            if (pageRef.current) pdfViewer.currentPageNumber = pageRef.current;
            updatePageRects();
        };
        const handlePageChanging = (event) => {
            setPageInput(String(event.pageNumber));
            if (pageRef.current !== event.pageNumber) {
                onPageChangeRef.current(event.pageNumber);
            }
        };
        const handleScaleChanging = (event) => {
            setScaleValue(event.presetValue || String(Math.round(event.scale * 100) / 100));
            window.requestAnimationFrame(updatePageRects);
        };
        const handlePageRendered = () => {
            updatePageRects();
        };

        eventBus.on("pagesinit", handlePagesInit);
        eventBus.on("pagechanging", handlePageChanging);
        eventBus.on("scalechanging", handleScaleChanging);
        eventBus.on("pagerendered", handlePageRendered);
        eventBus.on("textlayerrendered", handlePageRendered);

        let resizeFrame = null;
        const handleContainerResize = () => {
            if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
            resizeFrame = window.requestAnimationFrame(() => {
                if (["page-width", "page-fit", "auto"].includes(scaleValueRef.current)) {
                    pdfViewer.currentScaleValue = scaleValueRef.current;
                }
                updatePageRects();
            });
        };

        const resizeObserver = new ResizeObserver(handleContainerResize);
        resizeObserver.observe(container);

        return () => {
            if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
            resizeObserver.disconnect();
            eventBus.off("pagesinit", handlePagesInit);
            eventBus.off("pagechanging", handlePageChanging);
            eventBus.off("scalechanging", handleScaleChanging);
            eventBus.off("pagerendered", handlePageRendered);
            eventBus.off("textlayerrendered", handlePageRendered);
            pdfViewer.cleanup();
            pdfViewer.setDocument(null);
            findController.setDocument(null);
            linkService.setDocument(null);
            eventBusRef.current = null;
            pdfViewerRef.current = null;
        };
    }, [updatePageRects]);

    useEffect(() => {
        let cancelled = false;
        let loadingTask = null;

        const loadPdf = async () => {
            if (!fileUrl || !pdfViewerRef.current) {
                setMaxPage(1);
                setErrorMessage("");
                setLoading(false);
                return;
            }

            setLoading(true);
            setErrorMessage("");
            try {
                const arrayBuffer = await getReviewPdfFile(fileUrl);
                pdfBufferRef.current = arrayBuffer;
                loadingTask = getDocument({ data: new Uint8Array(arrayBuffer) });
                loadingTaskRef.current = loadingTask;
                const document = await loadingTask.promise;
                if (cancelled) {
                    document.destroy?.();
                    return;
                }

                pdfViewerRef.current.setDocument(document);
                pdfViewerRef.current.linkService.setDocument(document, null);
                pdfViewerRef.current.findController?.setDocument(document);
                setMaxPage(document.numPages || 1);
                if (pageRef.current > document.numPages) onPageChangeRef.current(document.numPages || 1);
            } catch (error) {
                if (!cancelled) {
                    setErrorMessage("PDF 파일을 불러오지 못했습니다.");
                    setMaxPage(1);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadPdf();
        return () => {
            cancelled = true;
            loadingTask?.destroy?.();
            if (loadingTaskRef.current === loadingTask) loadingTaskRef.current = null;
        };
    }, [fileUrl, setMaxPage]);

    useEffect(() => {
        const pdfViewer = pdfViewerRef.current;
        if (!pdfViewer || !page || pdfViewer.currentPageNumber === page) return;
        pdfViewer.currentPageNumber = page;
    }, [page]);

    useEffect(() => {
        if (!searchBox?.page) return;
        const pdfViewer = pdfViewerRef.current;
        if (pdfViewer && pdfViewer.currentPageNumber !== searchBox.page) {
            pdfViewer.currentPageNumber = searchBox.page;
        }
    }, [searchBox]);

    useEffect(() => {
        if (selectedBox === null) {
            setBoxes((prevBoxes) => prevBoxes.filter((box) => box.id !== Constants.REVIEW_NEW_SELECTION_ID));
        }
    }, [selectedBox, setBoxes]);

    const handlePageInputCommit = () => {
        const nextPage = clamp(Number(pageInput) || 1, 1, maxPage);
        pdfViewerRef.current.currentPageNumber = nextPage;
    };

    const handleZoomChange = (nextScaleValue) => {
        setScaleValue(nextScaleValue);
        pdfViewerRef.current.currentScaleValue = nextScaleValue;
        window.requestAnimationFrame(updatePageRects);
    };

    const handleZoomStep = (direction) => {
        const pdfViewer = pdfViewerRef.current;
        if (!pdfViewer) return;
        const nextScale = clamp(pdfViewer.currentScale * (direction === "in" ? 1.1 : 0.9), 0.25, 4);
        pdfViewer.currentScaleValue = String(nextScale);
        setScaleValue(String(nextScale));
    };

    const zoomOptions = [
        { value: "0.75", label: "75%" },
        { value: "1", label: "100%" },
        { value: "page-width", label: "너비" },
        { value: "page-fit", label: "맞춤" },
        { value: "1.25", label: "125%" },
        { value: "1.5", label: "150%" },
        { value: "2", label: "200%" },
    ];

    const handleDownload = () => {
        if (!pdfBufferRef.current || !fileUrl) return;
        const fileName = decodeURI(fileUrl.split("/").pop() || "document.pdf");
        saveAs(new Blob([pdfBufferRef.current], { type: "application/pdf" }), fileName);
    };

    const dispatchFind = (findPrevious = false) => {
        const query = searchKeyword.trim();
        if (!query || !eventBusRef.current) return;
        eventBusRef.current.dispatch("find", {
            source: pdfViewerRef.current,
            type: "again",
            query,
            phraseSearch: true,
            caseSensitive: false,
            entireWord: false,
            highlightAll: true,
            findPrevious,
        });
    };

    const clearFind = () => {
        setSearchKeyword("");
        eventBusRef.current?.dispatch("findbarclose", {
            source: pdfViewerRef.current,
        });
    };

    const handleMouseDown = (event) => {
        if (isLayoutPending) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        if (event.button !== 0) return;
        const pointerInfo = getPointerInfo(event);
        if (!pointerInfo) return;

        if (event.target.closest(".textLayer span")) {
            selectionInProgressRef.current = true;
            dragStartRef.current = {
                ...pointerInfo,
                selectionType: "text",
                layoutBox: getLayoutBoxAtPointer(pointerInfo),
            };
            setDragBox(null);
            setDragPage(null);
            return;
        }

        event.preventDefault();
        setSelectedBox(null);
        setPendingSelection(null);
        window.getSelection()?.removeAllRanges?.();
        selectionInProgressRef.current = false;
        dragStartRef.current = {
            ...pointerInfo,
            selectionType: "area",
            layoutBox: getLayoutBoxAtPointer(pointerInfo),
        };
        setDragPage(pointerInfo.pageNumber);
        setDragBox(null);
    };

    const handleMouseMove = (event) => {
        if (isLayoutPending) {
            setHoveredLayoutBox(null);
            return;
        }
        if (selectionInProgressRef.current) {
            setHoveredLayoutBox(null);
            return;
        }

        if (!dragStartRef.current) {
            const pointerInfo = getPointerInfo(event);
            if (!pointerInfo) {
                setHoveredLayoutBox(null);
                return;
            }

            const nextLayoutBox = getLayoutBoxAtPointer(pointerInfo);

            setHoveredLayoutBox(
                nextLayoutBox
                    ? {
                          page: pointerInfo.pageNumber,
                          ...nextLayoutBox,
                      }
                    : null,
            );
            return;
        }

        setHoveredLayoutBox(null);
        event.preventDefault();
        const current = getPointerInfo(event);
        if (!current || current.pageNumber !== dragStartRef.current.pageNumber) return;

        setDragBox({
            x1: Math.min(dragStartRef.current.x, current.x),
            y1: Math.min(dragStartRef.current.y, current.y),
            x2: Math.max(dragStartRef.current.x, current.x),
            y2: Math.max(dragStartRef.current.y, current.y),
        });
    };

    const handleMouseUp = async (event) => {
        if (isLayoutPending) {
            dragStartRef.current = null;
            selectionInProgressRef.current = false;
            setDragBox(null);
            setDragPage(null);
            return;
        }

        const dragStart = dragStartRef.current;
        const pointerInfo = getPointerInfo(event);
        const pointerDragBox =
            pointerInfo && pointerInfo.pageNumber === dragStart?.pageNumber
                ? {
                      x1: Math.min(dragStart.x, pointerInfo.x),
                      y1: Math.min(dragStart.y, pointerInfo.y),
                      x2: Math.max(dragStart.x, pointerInfo.x),
                      y2: Math.max(dragStart.y, pointerInfo.y),
                  }
                : null;
        const finalDragBox = dragBox || pointerDragBox;
        const hasDragBox =
            finalDragBox &&
            Math.abs(finalDragBox.x2 - finalDragBox.x1) >= MIN_DRAG_DISTANCE &&
            Math.abs(finalDragBox.y2 - finalDragBox.y1) >= MIN_DRAG_DISTANCE;

        if (dragStart?.selectionType === "text") {
            if (!hasDragBox && dragStart.layoutBox?.bbox) {
                const selection = {
                    selection_type: "area",
                    page: dragStart.pageNumber,
                    bbox: dragStart.layoutBox.bbox,
                };
                console.log("[PDF selection]", selection);
                await createTemporaryBox(dragStart.pageNumber, dragStart.layoutBox.bbox, {
                    selection_type: "area",
                    layout_label: dragStart.layoutBox.label,
                    color: selectionColor,
                });
                showSelectionComposer(selection);
                selectionInProgressRef.current = false;
                dragStartRef.current = null;
                setDragBox(null);
                setDragPage(null);
                return;
            }

            const textSelection = getSelectedTextSpans();
            if (textSelection.text || textSelection.spans.length > 0) {
                const bbox = getTextSpansUnionBbox(textSelection.spans);
                const selection = {
                    selection_type: "text",
                    page: dragStart.pageNumber,
                    bbox,
                    text_spans: textSelection.spans,
                };
                console.log("[PDF selection]", selection);
                if (bbox) showSelectionComposer(selection);
            }
            selectionInProgressRef.current = false;
            dragStartRef.current = null;
            setDragBox(null);
            setDragPage(null);
            return;
        }

        if (selectionInProgressRef.current) {
            selectionInProgressRef.current = false;
            dragStartRef.current = null;
            setDragBox(null);
            setDragPage(null);
            return;
        }

        if (!dragStart || !dragPage) {
            dragStartRef.current = null;
            setDragBox(null);
            setDragPage(null);
            return;
        }

        if (!hasDragBox) {
            if (dragStart.layoutBox?.bbox) {
                const selection = {
                    selection_type: "area",
                    page: dragStart.pageNumber,
                    bbox: dragStart.layoutBox.bbox,
                };
                console.log("[PDF selection]", selection);
                await createTemporaryBox(dragStart.pageNumber, dragStart.layoutBox.bbox, {
                    selection_type: "area",
                    layout_label: dragStart.layoutBox.label,
                    color: selectionColor,
                });
                showSelectionComposer(selection);
            }
            dragStartRef.current = null;
            setDragBox(null);
            setDragPage(null);
            return;
        }

        const areaBbox = [finalDragBox.x1, finalDragBox.y1, finalDragBox.x2, finalDragBox.y2];
        const selection = {
            selection_type: "area",
            page: dragPage,
            bbox: areaBbox,
        };
        console.log("[PDF selection]", selection);
        await createTemporaryBox(dragPage, areaBbox, {
            selection_type: "area",
            color: selectionColor,
        });
        showSelectionComposer(selection);
        dragStartRef.current = null;
        setDragBox(null);
        setDragPage(null);
    };

    const getBoxMessages = (box) => {
        const boxMessages = [];
        threads
            .filter((thread) => !thread.is_deleted)
            .forEach((thread) => {
                boxMessages.push(
                    ...thread.messages.filter((msg) => msg.is_deleted === false && msg.review_selection?.id === box.id),
                );
            });
        return boxMessages;
    };

    const getMessageAuthorName = (message) => {
        if (message.role === Constants.REVIEW_ROLE_AI) return "@PREP";
        const profile = (profiles || []).find((p) => p.id === message.space_member);
        return profile ? `@${profile.name}` : "사용자";
    };

    const getMessagePreview = (message) =>
        (message.content || "")
            .replace(/@\[([^\]]+)\]\(([^)]+)\)/g, "@$1")
            .replace(/\s+/g, " ")
            .trim();

    const handleSelectMessage = (message) => {
        setSearchBox(null);
        onSelectReviewMessage?.(message);
    };

    const renderBox = (box, idx) => {
        if (!box?.data?.bbox || !pageRects[box.page]) return null;

        const pageRect = pageRects[box.page];
        const [x1, y1, x2, y2] = box.data.bbox;
        const left = pageRect.left + x1 * pageRect.width;
        const top = pageRect.top + y1 * pageRect.height;
        const width = (x2 - x1) * pageRect.width;
        const height = (y2 - y1) * pageRect.height;
        const isHovered = hoveredBoxIdx === idx;
        const boxMessages = getBoxMessages(box);
        const messageCount = boxMessages.length;
        const boxColor = box.data.color || "var(--color-Key)";

        if (messageCount === 0 && box.id !== Constants.REVIEW_NEW_SELECTION_ID) return null;

        const handleSearchBox = (event) => {
            event.stopPropagation();
            setHoveredBoxIdx(null);
            setSelectedBox(null);
            setSearchBox(box);
        };

        if (box.selection_type === "text" && box.data?.text_spans?.length) {
            return (
                <React.Fragment key={`${box.id}-${idx}`}>
                    {box.data.text_spans.map((span, spanIndex) => {
                        if (!span?.bbox || !pageRects[span.page]) return null;

                        const spanPageRect = pageRects[span.page];
                        const [sx1, sy1, sx2, sy2] = span.bbox;
                        return (
                            <ReviewTextSelectionBox
                                key={`${box.id}-${span.page}-${span.index ?? spanIndex}`}
                                $hovered={isHovered}
                                $color={boxColor}
                                style={{
                                    left: spanPageRect.left + sx1 * spanPageRect.width,
                                    top: spanPageRect.top + sy1 * spanPageRect.height,
                                    width: (sx2 - sx1) * spanPageRect.width,
                                    height: (sy2 - sy1) * spanPageRect.height,
                                }}
                                onMouseEnter={() => setHoveredBoxIdx(idx)}
                                onMouseLeave={() => setHoveredBoxIdx(null)}
                                onClick={handleSearchBox}
                            />
                        );
                    })}
                    {isHovered && (
                        <BoxButtonGroup
                            style={{
                                left: pageRect.left + x2 * pageRect.width - 72,
                                top: top + height,
                            }}
                            onMouseEnter={() => setHoveredBoxIdx(idx)}
                            onMouseLeave={() => setHoveredBoxIdx(null)}
                        >
                            <BoxButtonText onClick={handleSearchBox}>{messageCount} messages</BoxButtonText>
                        </BoxButtonGroup>
                    )}
                </React.Fragment>
            );
        }

        return (
            <React.Fragment key={`${box.id}-${idx}`}>
                <ReviewBox
                    $active={messageCount > 0 || box.id === Constants.REVIEW_NEW_SELECTION_ID}
                    $filled={box.id === Constants.REVIEW_NEW_SELECTION_ID}
                    $hovered={isHovered}
                    $color={boxColor}
                    style={{ left, top, width, height }}
                    onMouseEnter={() => setHoveredBoxIdx(idx)}
                    onMouseLeave={() => setHoveredBoxIdx(null)}
                    onClick={(event) => {
                        event.stopPropagation();
                        if (box.id !== Constants.REVIEW_NEW_SELECTION_ID) {
                            handleSearchBox(event);
                            return;
                        }
                        setSelectedBox({
                            ...box,
                            box_image: getBoxImageFromCanvas(box.page, box.data.bbox),
                        });
                    }}
                />
                {isHovered && (
                    <BoxButtonGroup
                        style={{
                            left: pageRect.left + x2 * pageRect.width - 72,
                            top: top + height,
                        }}
                        onMouseEnter={() => setHoveredBoxIdx(idx)}
                        onMouseLeave={() => setHoveredBoxIdx(null)}
                    >
                        <BoxButtonText onClick={handleSearchBox}>{messageCount} messages</BoxButtonText>
                    </BoxButtonGroup>
                )}
            </React.Fragment>
        );
    };

    const renderLayoutHoverBox = () => {
        if (selectionInProgressRef.current || pendingSelection?.selection_type === "text") return null;
        if (!hoveredLayoutBox?.bbox || !pageRects[hoveredLayoutBox.page]) return null;

        const pageRect = pageRects[hoveredLayoutBox.page];
        const [x1, y1, x2, y2] = hoveredLayoutBox.bbox;
        return (
            <LayoutHoverBox
                style={{
                    left: pageRect.left + x1 * pageRect.width,
                    top: pageRect.top + y1 * pageRect.height,
                    width: (x2 - x1) * pageRect.width,
                    height: (y2 - y1) * pageRect.height,
                }}
            />
        );
    };

    const renderDragBox = () => {
        if (!dragBox || !dragPage || !pageRects[dragPage]) return null;
        const pageRect = pageRects[dragPage];
        return (
            <DragBox
                style={{
                    left: pageRect.left + dragBox.x1 * pageRect.width,
                    top: pageRect.top + dragBox.y1 * pageRect.height,
                    width: (dragBox.x2 - dragBox.x1) * pageRect.width,
                    height: (dragBox.y2 - dragBox.y1) * pageRect.height,
                }}
            />
        );
    };

    const renderPendingTextSelection = () => {
        if (pendingSelection?.selection_type !== "text") return null;
        const spans = pendingSelection.text_spans || [];
        if (!spans.length) return null;

        return spans.map((span, index) => {
            if (!span?.bbox || !pageRects[span.page]) return null;

            const pageRect = pageRects[span.page];
            const [x1, y1, x2, y2] = span.bbox;
            return (
                <PendingTextSelectionBox
                    key={`${span.page}-${span.index ?? index}`}
                    $color={pendingSelection.color || selectionColor}
                    style={{
                        left: pageRect.left + x1 * pageRect.width,
                        top: pageRect.top + y1 * pageRect.height,
                        width: (x2 - x1) * pageRect.width,
                        height: (y2 - y1) * pageRect.height,
                    }}
                />
            );
        });
    };

    const renderSearchBox = () => {
        if (!searchBox?.data?.bbox || !pageRects[searchBox.page]) return null;
        const pageRect = pageRects[searchBox.page];
        const [x1, y1, x2, y2] = searchBox.data.bbox;
        const messages = getBoxMessages(searchBox);
        const panelWidth = 300;
        const panelMaxHeight = 240;
        const panelLeft = Math.min(
            Math.max(pageRect.left + x2 * pageRect.width + 8, 8),
            Math.max(8, overlaySize.width - panelWidth - 8),
        );
        const panelTop = Math.min(
            Math.max(pageRect.top + y2 * pageRect.height + 8, 8),
            Math.max(8, overlaySize.height - panelMaxHeight - 8),
        );
        return (
            <>
                <DimOverlay onClick={() => setSearchBox(null)} />
                <HighlightBox
                    style={{
                        left: pageRect.left + x1 * pageRect.width,
                        top: pageRect.top + y1 * pageRect.height,
                        width: (x2 - x1) * pageRect.width,
                        height: (y2 - y1) * pageRect.height,
                    }}
                />
                <SelectionMessagePanel
                    style={{ left: panelLeft, top: panelTop }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <SelectionMessagePanelHeader>
                        <Icon name="thread" size="13" color="var(--color-SubBlack)" />
                        <SelectionMessagePanelTitle>연결된 메시지</SelectionMessagePanelTitle>
                        <SelectionMessagePanelMeta>{messages.length}건</SelectionMessagePanelMeta>
                    </SelectionMessagePanelHeader>
                    <SelectionMessageList>
                        {messages.length === 0 ? (
                            <SelectionMessageItem type="button" disabled>
                                <SelectionMessagePreview>연결된 메시지가 없습니다.</SelectionMessagePreview>
                            </SelectionMessageItem>
                        ) : (
                            messages.map((message) => (
                                <SelectionMessageItem
                                    key={message.id}
                                    type="button"
                                    onClick={() => handleSelectMessage(message)}
                                >
                                    <SelectionMessageAuthor>{getMessageAuthorName(message)}</SelectionMessageAuthor>
                                    <SelectionMessagePreview>{getMessagePreview(message)}</SelectionMessagePreview>
                                </SelectionMessageItem>
                            ))
                        )}
                    </SelectionMessageList>
                </SelectionMessagePanel>
            </>
        );
    };

    const handleComposerSubmit = () => {
        const message = composerMessage.trim();
        if (!message || !pendingSelection) return;

        console.log("[PDF selection message]", {
            selection: pendingSelection,
            message,
            auto_response: composerAutoResponse,
        });
        chatStart?.({
            autoResponse: composerAutoResponse,
            content: message,
            reviewSelectionId: pendingSelection.id || Constants.REVIEW_NEW_SELECTION_ID,
            reviewSelectionColor: pendingSelection.color || selectionColor,
            newReviewSelectionData: pendingSelection.id
                ? null
                : {
                      page: pendingSelection.page,
                      selection_type: pendingSelection.selection_type,
                      data: {
                          bbox: pendingSelection.bbox,
                          text_spans: pendingSelection.text_spans || undefined,
                          color: pendingSelection.color || selectionColor,
                      },
                  },
            parentMessageId: parentMessageId || null,
        });
        closeSelectionComposer();
    };

    const renderSelectionComposer = () => {
        if (!pendingSelection?.bbox || !pageRects[pendingSelection.page]) return null;

        const pageRect = pageRects[pendingSelection.page];
        const [, y1, x2, y2] = pendingSelection.bbox;
        const anchorLeft = pageRect.left + x2 * pageRect.width + 8;
        const anchorTop = pageRect.top + y2 * pageRect.height + 8;
        const maxLeft = Math.max(0, overlaySize.width - COMPOSER_WIDTH - 8);
        const maxTop = Math.max(0, overlaySize.height - COMPOSER_HEIGHT - 8);
        const left = clamp(anchorLeft, 8, maxLeft);
        const top =
            anchorTop + COMPOSER_HEIGHT > overlaySize.height
                ? clamp(pageRect.top + y1 * pageRect.height - COMPOSER_HEIGHT - 8, 8, maxTop)
                : clamp(anchorTop, 8, maxTop);

        return (
            <SelectionComposer
                style={{ left, top }}
                onMouseDown={(event) => event.stopPropagation()}
                onMouseUp={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
            >
                <ComposerHeader>
                    <ComposerHeaderLeft>
                        <ComposerTitle>선택 영역</ComposerTitle>
                        <ComposerColorPalette>
                            {HIGHLIGHT_COLORS.map((color) => (
                                <ComposerColorButton
                                    key={color}
                                    type="button"
                                    $color={color}
                                    $active={(pendingSelection.color || selectionColor) === color}
                                    onClick={() => handleSelectionColorChange(color)}
                                    title="선택 색상"
                                />
                            ))}
                        </ComposerColorPalette>
                    </ComposerHeaderLeft>
                    <ComposerCloseButton type="button" onClick={closeSelectionComposer} title="닫기">
                        ×
                    </ComposerCloseButton>
                </ComposerHeader>
                <ComposerTextarea
                    value={composerMessage}
                    onChange={(event) => setComposerMessage(event.target.value)}
                    placeholder="이 영역에 대해 메시지를 남겨보세요."
                />
                <ComposerOptionRow>
                    <Checkbox
                        label="AI 자동 응답"
                        size="small"
                        checked={composerAutoResponse}
                        onChange={() => setComposerAutoResponse((prev) => !prev)}
                    />
                </ComposerOptionRow>
                <ComposerFooter>
                    <ComposerButton type="button" onClick={closeSelectionComposer}>
                        취소
                    </ComposerButton>
                    <ComposerButton
                        type="button"
                        $primary
                        disabled={!composerMessage.trim()}
                        onClick={handleComposerSubmit}
                    >
                        전송
                    </ComposerButton>
                </ComposerFooter>
            </SelectionComposer>
        );
    };

    return (
        <ViewerRoot>
            <TopToolbar>
                <ToolbarCenter>
                    <ToolbarGroup>
                        <ToolbarButton onClick={() => handleZoomStep("out")} title="축소">
                            −
                        </ToolbarButton>
                        <ToolbarSelect
                            value={scaleValue}
                            onChange={(event) => handleZoomChange(event.target.value)}
                            title="확대 비율"
                        >
                            {zoomOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </ToolbarSelect>
                        <ToolbarButton onClick={() => handleZoomStep("in")} title="확대">
                            ＋
                        </ToolbarButton>
                    </ToolbarGroup>
                    <ToolbarDivider />
                    <ToolbarGroup>
                        <ToolbarButton
                            onClick={() => {
                                pdfViewerRef.current.currentPageNumber = Math.max(1, page - 1);
                            }}
                            disabled={page <= 1}
                            title="이전 페이지"
                        >
                            ◀
                        </ToolbarButton>
                        <ToolbarInput
                            type="number"
                            min={1}
                            max={maxPage}
                            value={pageInput}
                            onChange={(event) => setPageInput(event.target.value)}
                            onBlur={handlePageInputCommit}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") handlePageInputCommit();
                            }}
                        />
                        <ToolbarText>/</ToolbarText>
                        <ToolbarText>{maxPage}</ToolbarText>
                        <ToolbarButton
                            onClick={() => {
                                pdfViewerRef.current.currentPageNumber = Math.min(maxPage, page + 1);
                            }}
                            disabled={page >= maxPage}
                            title="다음 페이지"
                        >
                            ▶
                        </ToolbarButton>
                    </ToolbarGroup>
                    <ToolbarDivider />
                    <ToolbarGroup>
                        <ToolbarButton onClick={handleDownload} title="다운로드">
                            <Icon name="download" size="13" color="var(--color-SubBlack)" />
                        </ToolbarButton>
                        <SearchWrap ref={searchWrapRef}>
                            <ToolbarButton onClick={() => setIsSearchOpen((prev) => !prev)} title="검색">
                                <Icon name="search" size="13" color="var(--color-SubBlack)" />
                            </ToolbarButton>
                            {isSearchOpen && (
                                <SearchPopover>
                                    <SearchInput
                                        value={searchKeyword}
                                        placeholder="PDF 검색"
                                        autoFocus
                                        onChange={(event) => setSearchKeyword(event.target.value)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") dispatchFind(event.shiftKey);
                                        }}
                                    />
                                    <ToolbarButton onClick={() => dispatchFind(true)} title="이전 결과">
                                        ◀
                                    </ToolbarButton>
                                    <ToolbarButton onClick={() => dispatchFind(false)} title="다음 결과">
                                        ▶
                                    </ToolbarButton>
                                    <ToolbarButton onClick={clearFind} title="검색 초기화">
                                        ×
                                    </ToolbarButton>
                                </SearchPopover>
                            )}
                        </SearchWrap>
                    </ToolbarGroup>
                </ToolbarCenter>
                <ToolbarSide>
                    {isLayoutPending && (
                        <ToolbarStatus>
                            <ToolbarStatusDot />
                            <ToolbarStatusText>레이아웃 분석 중</ToolbarStatusText>
                        </ToolbarStatus>
                    )}
                </ToolbarSide>
            </TopToolbar>
            <ViewerBody>
                <ViewerContainer
                    ref={containerRef}
                    $isLayoutPending={isLayoutPending}
                    $selectionColor={pendingSelection?.color || selectionColor}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={() => setHoveredLayoutBox(null)}
                >
                    <ViewerElement ref={viewerRef} className="pdfViewer" />
                    <OverlayLayer $width={overlaySize.width} $height={overlaySize.height}>
                        {renderLayoutHoverBox()}
                        {boxes.map(renderBox)}
                        {renderDragBox()}
                        {renderPendingTextSelection()}
                        {renderSearchBox()}
                        {renderSelectionComposer()}
                    </OverlayLayer>
                    {loading && <ViewerStatus>PDF 파일을 불러오는 중입니다.</ViewerStatus>}
                    {errorMessage && <ViewerStatus>{errorMessage}</ViewerStatus>}
                </ViewerContainer>
            </ViewerBody>
        </ViewerRoot>
    );
}

export default PDFJsReviewViewer;
